<template>
  <div class="container py-5 my-history">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Mon historique</h2>
      <b-button variant="outline-primary" size="sm" :disabled="loading" @click="fetchHistory">
        <b-icon icon="arrow-clockwise" class="mr-1" />
        Rafraîchir
      </b-button>
    </div>

    <b-alert v-if="error" variant="danger" show class="mb-4">{{ error }}</b-alert>

    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Chargement..." />
    </div>

    <template v-else>
      <!-- Métriques -->
      <div class="row mb-4">
        <div class="col-12 col-md-4 mb-3">
          <div class="stat-card">
            <div class="stat-card__label">Sessions au total</div>
            <div class="stat-card__value">{{ sessions.length }}</div>
          </div>
        </div>
        <div class="col-12 col-md-4 mb-3">
          <div class="stat-card">
            <div class="stat-card__label">Dernière session</div>
            <div class="stat-card__value">
              {{ sessions.length ? formatDate(sessions[0].date) : '—' }}
            </div>
          </div>
        </div>
        <div class="col-12 col-md-4 mb-3">
          <div class="stat-card">
            <div class="stat-card__label">Voile la plus utilisée</div>
            <div class="stat-card__value">{{ topSail }}</div>
          </div>
        </div>
      </div>

      <!-- Table desktop -->
      <b-alert v-if="!sessions.length" variant="info" show>
        Aucune session passée pour le moment.
      </b-alert>

      <div v-else class="table-responsive d-none d-md-block">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>Date</th>
              <th>Ouverture</th>
              <th>Ouvreur(s)</th>
              <th>Voile(s)</th>
              <th>Planche(s)</th>
              <th>Équipement</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="session in sessions" :key="session.id">
              <td class="text-nowrap">{{ formatDate(session.date) }}</td>
              <td>{{ session.title || '—' }}</td>
              <td>{{ formatOwners(session.owners) }}</td>
              <td>{{ formatSails(session.sailsUsed) }}</td>
              <td>{{ formatBoards(session.boardsUsed) }}</td>
              <td>{{ formatEquipement(session) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards mobile -->
      <div class="d-md-none">
        <b-card
          v-for="session in sessions"
          :key="`card-${session.id}`"
          class="history-card mb-3"
        >
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div class="history-card__title">{{ session.title || '—' }}</div>
              <div class="text-muted small">{{ formatDate(session.date) }}</div>
            </div>
          </div>
          <div class="history-card__meta">
            <div class="history-card__row">
              <span class="history-card__label">Ouvreur(s)</span>
              <span class="history-card__value">{{ formatOwners(session.owners) }}</span>
            </div>
            <div class="history-card__row">
              <span class="history-card__label">Voile(s)</span>
              <span class="history-card__value">{{ formatSails(session.sailsUsed) }}</span>
            </div>
            <div class="history-card__row">
              <span class="history-card__label">Planche(s)</span>
              <span class="history-card__value">{{ formatBoards(session.boardsUsed) }}</span>
            </div>
            <div class="history-card__row">
              <span class="history-card__label">Équipement</span>
              <span class="history-card__value">{{ formatEquipement(session) }}</span>
            </div>
          </div>
        </b-card>
      </div>
    </template>
  </div>
</template>

<script>
import OpeningService from '../services/opening.service';

export default {
  name: 'MyHistory',
  data() {
    return {
      sessions: [],
      loading: true,
      error: null,
    };
  },
  mounted() {
    this.fetchHistory();
  },
  computed: {
    topSail() {
      const counts = {};
      this.sessions.forEach(s => {
        (s.sailsUsed || []).forEach(sail => {
          const key = this.formatSail(sail);
          counts[key] = (counts[key] || 0) + 1;
        });
      });
      const entries = Object.entries(counts);
      if (!entries.length) return '—';
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    },
  },
  methods: {
    async fetchHistory() {
      this.loading = true;
      this.error = null;
      try {
        const res = await OpeningService.getMyHistory();
        if (res.status !== 200) throw new Error();
        this.sessions = res.data;
      } catch (err) {
        this.error = 'Impossible de récupérer votre historique.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
    formatOwners(owners) {
      if (!owners || !owners.length) return '—';
      return owners
        .map(o => [o.name, o.surname].filter(Boolean).join(' ') || o.email)
        .join(', ');
    },
    formatSail(sail) {
      if (!sail || typeof sail !== 'object') return 'Voile';
      const parts = [];
      if (sail.guc_id) parts.push(sail.guc_id);
      if (sail.brand) parts.push(sail.brand);
      if (sail.size) parts.push(sail.size + 'm²');
      return parts.length ? parts.join(' · ') : 'Voile';
    },
    formatSails(sails) {
      if (!sails || !sails.length) return '—';
      return sails.map(this.formatSail).join(', ');
    },
    formatBoards(boards) {
      if (!boards || !boards.length) return '—';
      return boards.map(b => {
        if (!b || typeof b !== 'object') return 'Planche';
        const parts = [];
        if (b.brand) parts.push(b.brand);
        if (b.volume) parts.push(b.volume + 'L');
        return parts.length ? parts.join(' · ') : 'Planche';
      }).join(', ');
    },
    formatEquipement(session) {
      const parts = [];
      if (session.harnais) parts.push('Harnais');
      if (session.combi) parts.push('Combi');
      return parts.length ? parts.join(', ') : '—';
    },
  },
};
</script>

<style scoped>
.stat-card {
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 0.75rem;
  padding: 1.25rem;
  height: 100%;
}

.stat-card__label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.4rem;
}

.stat-card__value {
  font-size: 1.4rem;
  font-weight: 600;
  color: #111827;
}

.history-card {
  border: 1px solid #eef1f5;
  border-radius: 0.75rem;
  box-shadow: none;
}

.history-card__title {
  font-weight: 600;
}

.history-card__meta {
  border-top: 1px solid #f1f3f6;
  padding-top: 0.5rem;
}

.history-card__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.2rem 0;
}

.history-card__label {
  color: #6b7280;
  font-size: 0.85rem;
  margin-right: 0.75rem;
}

.history-card__value {
  font-weight: 600;
  text-align: right;
}
</style>
